/* ============================================================================
   rbk-cursor.js — interactive custom cursor (v3).
   Enabled + variant chosen in Theme settings -> Cursor (layout/theme.liquid adds
   html.rbk-cur--<style> and only loads this + rbk-cursor.css when enabled).
   Fine pointers only; never on touch; reduced-motion = instant (no trail).

   Behaviour: the DOT sits exactly on the pointer every frame; the RING trails
   with easing while moving and SNAPS concentric the moment movement stops, so
   a static cursor is always perfectly centered. JS owns ONLY the 0x0 shells'
   translate3d; all visuals are children centered by rbk-cursor.css with the
   independent `translate` property (see the centering contract there).
   States: grow over links, label pill (Drag/View/Slide) hides the dot,
   native over inputs/iframes, press squish on mousedown.
   Block comments only. ROLLBACK: delete file + its tags in theme.liquid.
   ========================================================================== */
(function () {
  'use strict';
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var ring = document.createElement('div');
  var dot = document.createElement('div');
  ring.className = 'rbk-cur-ring';
  dot.className = 'rbk-cur-dot';
  ring.innerHTML = '<span class="rbk-cur-ring__v"></span><span class="rbk-cur-ring__label"></span>';
  dot.innerHTML = '<span class="rbk-cur-dot__v"></span>';
  var label = ring.querySelector('.rbk-cur-ring__label');
  document.body.appendChild(ring);
  document.body.appendChild(dot);
  document.documentElement.classList.add('rbk-cursor-on');

  var x = innerWidth / 2, y = innerHeight / 2, rx = x, ry = y, seen = false, raf = null;
  var LERP = REDUCE ? 1 : 0.2;

  /* STALL FAIL-SAFE: if rAF stops ticking (GPU hiccup, long task, frozen
     renderer) while cursor:none is active, the user points blind and the
     store reads as jammed. mousemove keeps firing even when rAF doesn't,
     so it doubles as the watchdog: stale heartbeat -> html.rbk-cur-stall
     (CSS restores the native cursor instantly). tick() clears the flag
     the moment frames flow again. */
  var lastTick = performance.now(), stalled = false;

  function tick() {
    raf = requestAnimationFrame(tick);
    lastTick = performance.now();
    if (stalled) { stalled = false; document.documentElement.classList.remove('rbk-cur-stall'); }
    var ex = x - rx, ey = y - ry;
    rx += ex * LERP; ry += ey * LERP;
    /* snap concentric when essentially settled so a STATIC cursor is never offset */
    if (Math.abs(ex) < 0.4 && Math.abs(ey) < 0.4) { rx = x; ry = y; }
    dot.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';
    ring.style.transform = 'translate3d(' + rx + 'px,' + ry + 'px,0)';
  }
  raf = requestAnimationFrame(tick);

  document.addEventListener('mousemove', function (e) {
    x = e.clientX; y = e.clientY;
    /* exact element tracks straight from the event too, so the dot follows
       even when rAF frames are being throttled */
    dot.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';
    if (!stalled && performance.now() - lastTick > 600) {
      stalled = true; document.documentElement.classList.add('rbk-cur-stall');
    }
    /* first sighting: teleport the ring so it never flies in from a corner */
    if (!seen) { seen = true; rx = x; ry = y; dot.classList.add('is-on'); ring.classList.add('is-on'); }
  }, { passive: true });
  /* pointermove ALSO drives position. During a scrub/drag the widget calls
     setPointerCapture, which diverts mousemove to that element — so the
     document-level mousemove above stops firing and the cursor would freeze
     mid-scrub. pointermove still bubbles to document under capture, so the
     dot keeps following the thumb. (Fires alongside mousemove normally; the
     double-write is harmless.) */
  document.addEventListener('pointermove', function (e) {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    x = e.clientX; y = e.clientY;
    dot.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';
    if (!seen) { seen = true; rx = x; ry = y; dot.classList.add('is-on'); ring.classList.add('is-on'); }
  }, { passive: true });
  document.addEventListener('mouseleave', function () { dot.classList.remove('is-on'); ring.classList.remove('is-on'); seen = false; });
  document.addEventListener('mouseenter', function () { if (seen) { dot.classList.add('is-on'); ring.classList.add('is-on'); } });

  function setState(mode, text) {
    ring.setAttribute('data-mode', mode || '');
    dot.setAttribute('data-mode', mode || '');
    label.textContent = text || '';
  }
  document.addEventListener('mouseover', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('input, textarea, select, iframe, .shopify-section-group-header-group')) { setState('native', ''); return; }
    /* zones that own their OWN cursor (PDP layout-3 gallery shows a "swipe"
       circle) — fully yield so two cursors never overlap (the white halo dot
       was clipping the swipe label) */
    if (t.closest('.rbk-l3-viewport')) { setState('native', ''); return; }
    var drag = t.closest('.rp-gallery__strip, [data-rp-jr-track], .r-rec-grid, [data-rp-collage], [data-rbk-testimonial-track], .is-ymal-scroll .rbk-ymal__grid, .rbk-worlds--scrollable .rbk-worlds__grid[data-layout="scroll"]');
    if (drag) { setState('label', 'Drag'); return; }
    if (t.closest('[data-rp-compare]')) { setState('label', 'Slide'); return; }
    if (t.closest('a.rp-pc, .rp-wx__card, .rp-lf__card, product-item, .cp-card-link, .rp-wx__row')) { setState('label', 'View'); return; }
    if (t.closest('a, button, [role="button"], label, summary, .rp-faq__q, .rp-stripbar')) { setState('grow', ''); return; }
    setState('', '');
  }, { passive: true });

  /* magnetic buttons: gentle pull, transform-only, resets on leave */
  if (!REDUCE) {
    document.addEventListener('mousemove', function (e) {
      var btn = e.target.closest && e.target.closest('.rp-btn');
      if (!btn) return;
      var r = btn.getBoundingClientRect();
      var dx = (e.clientX - (r.left + r.width / 2)) / r.width;
      var dy = (e.clientY - (r.top + r.height / 2)) / r.height;
      btn.style.transform = 'translate(' + (dx * 6).toFixed(1) + 'px,' + (dy * 5).toFixed(1) + 'px)';
      btn.__rbkMag = true;
    }, { passive: true });
    document.addEventListener('mouseout', function (e) {
      var btn = e.target.closest && e.target.closest('.rp-btn');
      if (btn && btn.__rbkMag && (!e.relatedTarget || !btn.contains(e.relatedTarget))) {
        btn.style.transform = ''; btn.__rbkMag = false;
      }
    }, { passive: true });
  }

  document.addEventListener('mousedown', function () { ring.classList.add('is-down'); dot.classList.add('is-down'); });
  document.addEventListener('mouseup', function () { ring.classList.remove('is-down'); dot.classList.remove('is-down'); });
  /* mouseup can be missed when focus leaves the window mid-press */
  window.addEventListener('blur', function () { ring.classList.remove('is-down'); dot.classList.remove('is-down'); });
  window.addEventListener('pagehide', function () { cancelAnimationFrame(raf); });
})();
